import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { BotState } from '@apiary/shared'
import type { BotDto, CommandAcceptedDto, Paginated } from '@/lib/dto'
import { api } from '@/lib/api'
import { qk } from '@/lib/query-keys'
import { describeError } from '@/lib/problem-message'
import { rememberCommand } from '@/lib/ws-cache'
import { DEFAULT_TOAST_MS, UNDO_TOAST_MS, useToastStore } from '@/stores/toasts'

/**
 * Команды бота (docs/10 §5): кнопка в loading, оптимистичный переход в `QUEUED`,
 * откат по `ack { ok: false }` (откат делает `ws-cache`, здесь только запоминаем
 * предыдущее состояние по `commandId`).
 *
 * `Idempotency-Key` ставится в `lib/api.ts` — повтор после refresh уйдёт с тем же.
 */

export type BotCommand = 'start' | 'stop' | 'restart'

function findState(cached: unknown, botId: string): BotState | null {
  const page = cached as Paginated<BotDto> | undefined
  const bot = page?.items.find(item => item.id === botId)
  return bot?.state ?? null
}

export function useBotCommand(): UseMutationResult<
  CommandAcceptedDto,
  unknown,
  { botId: string; botName: string; command: BotCommand }
> {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const push = useToastStore(state => state.push)

  return useMutation({
    mutationFn: ({ botId, command }) => {
      if (command === 'start') return api.bots.start(botId)
      if (command === 'stop') return api.bots.stop(botId)
      return api.bots.restart(botId)
    },
    onMutate: ({ botId, botName, command }) => {
      const detail = queryClient.getQueryData<BotDto>(qk.bots.detail(botId))
      const listState = queryClient
        .getQueriesData({ queryKey: qk.bots.lists })
        .map(([, data]) => findState(data, botId))
        .find((state): state is BotState => state !== null)
      const previousState: BotState = detail?.state ?? listState ?? 'UNKNOWN'

      // Оптимистичный переход: QUEUED для start/restart, STOPPED мы не показываем
      // сразу (бот ещё в игре) — тоже QUEUED, пока не придёт настоящее состояние.
      patchState(queryClient, botId, 'QUEUED')

      push({
        message: t(
          command === 'start'
            ? 'toast.botStarting'
            : command === 'stop'
              ? 'toast.botStopping'
              : 'toast.botRestarting',
          { name: botName }
        ),
        tone: 'info',
        timeoutMs: DEFAULT_TOAST_MS
      })

      return { previousState }
    },
    onSuccess: (data, variables, context) => {
      const previousState = (context as { previousState?: BotState } | undefined)?.previousState
      rememberCommand(data.commandId, variables.botId, previousState ?? 'UNKNOWN')
    },
    onError: (error, variables, context) => {
      const previousState = (context as { previousState?: BotState } | undefined)?.previousState
      if (previousState !== undefined) patchState(queryClient, variables.botId, previousState)
      push({
        message: t('toast.commandFailed', { reason: describeError(error, t).detail }),
        tone: 'danger',
        timeoutMs: UNDO_TOAST_MS
      })
    }
  })
}

function patchState(
  queryClient: ReturnType<typeof useQueryClient>,
  botId: string,
  state: BotState
): void {
  const at = new Date().toISOString()
  queryClient.setQueriesData<Paginated<BotDto>>({ queryKey: qk.bots.lists }, previous => {
    if (previous === undefined) return previous
    return {
      ...previous,
      items: previous.items.map(bot =>
        bot.id === botId ? { ...bot, state, lastStateAt: at } : bot
      )
    }
  })
  queryClient.setQueryData<BotDto>(qk.bots.detail(botId), previous =>
    previous === undefined ? previous : { ...previous, state, lastStateAt: at }
  )
}

export function useDeleteBot(): UseMutationResult<
  void,
  unknown,
  { botId: string; botName: string }
> {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const push = useToastStore(state => state.push)

  return useMutation({
    mutationFn: ({ botId }) => api.bots.remove(botId),
    onSuccess: (_data, { botName }) => {
      void queryClient.invalidateQueries({ queryKey: qk.bots.root })
      void queryClient.invalidateQueries({ queryKey: qk.dashboard.summary })
      push({
        message: t('toast.botDeleted', { name: botName }),
        tone: 'warning',
        timeoutMs: UNDO_TOAST_MS
      })
    },
    onError: error => {
      push({
        message: t('toast.commandFailed', { reason: describeError(error, t).detail }),
        tone: 'danger',
        timeoutMs: UNDO_TOAST_MS
      })
    }
  })
}
